import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Eye, EyeOff, Copy, Check, ShieldAlert, KeyRound } from 'lucide-react';
import {
  getSeityStatus,
  unlockSeity,
  recoverSeity,
  type SeityStatus,
} from '@/api/seityOnboarding';
import { EngineHttpError } from '@/lib/engineHttp';

// ─────────────────────────────────────────────────────────────────────────────
// SeityOnboardingGate
//
// First-run / post-login gate that runs before the authenticated app surfaces.
// It enforces the per-tenant seity unlock lifecycle (seitiate PR #76):
//
//   1. status check        -> GET  /seity/status
//   2. first-use set        -> POST /seity/unlock (first_use=true -> recovery_key)
//   3. one-time recovery    -> show recovery_key once, require confirmation
//   4. unlock (locked)      -> POST /seity/unlock
//   5. recover (forgot)     -> POST /seity/recover { recovery_key, new_passphrase }
//   6. unlocked             -> render children
//
// SECURITY INVARIANTS:
//   - The recovery key is shown exactly once (the only time the API returns it),
//     on an unmissable screen requiring explicit confirmation.
//   - Plaintext passphrase / recovery key live only in component form state and
//     the one-time display. Never localStorage, never logs, never query params.
// ─────────────────────────────────────────────────────────────────────────────

type Phase = 'loading' | 'error' | 'setup' | 'reveal' | 'unlock' | 'recover' | 'done';

function friendlyError(err: unknown, fallback: string): string {
  if (err instanceof EngineHttpError) {
    // 401 from the engine means a credential mismatch on this surface.
    if (err.status === 401) return fallback;
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

const cardStyle: React.CSSProperties = {
  background: 'var(--sp-bg-elev)',
  border: '1px solid var(--sp-border)',
  borderRadius: 8,
  padding: 32,
  width: 420,
  maxWidth: '100%',
};

function GateShell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      {children}
    </div>
  );
}

/**
 * The estate's one passphrase input: a password field with autocomplete so a
 * password manager can fill it, and a show/hide toggle. Exported so the desk's
 * in-session unlock (a locked seity mid-edit) reuses it rather than collecting a
 * credential through a second, weaker control.
 */
export function PassphraseField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  autoFocus?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <>
      <label className="sp-label" htmlFor={id}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          className="sp-input"
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          style={{ paddingRight: 44 }}
        />
        <button
          type="button"
          aria-label={show ? 'Hide' : 'Show'}
          onClick={() => setShow((v) => !v)}
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            border: 0,
            borderRadius: 6,
            background: 'transparent',
            color: 'var(--sp-text-dim)',
            cursor: 'pointer',
          }}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </>
  );
}

export function SeityOnboardingGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [statusError, setStatusError] = useState<string | null>(null);

  // Form state — plaintext lives only here, in memory, for the lifetime of the gate.
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [recoveryKeyInput, setRecoveryKeyInput] = useState('');
  const [newPassphrase, setNewPassphrase] = useState('');

  // One-time recovery key returned by the engine on first_use.
  const [revealedRecoveryKey, setRevealedRecoveryKey] = useState<string | null>(null);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const resetForms = useCallback(() => {
    setPassphrase('');
    setConfirmPassphrase('');
    setRecoveryKeyInput('');
    setNewPassphrase('');
    setFormError(null);
  }, []);

  const checkStatus = useCallback(async () => {
    setPhase('loading');
    setStatusError(null);
    try {
      const status: SeityStatus = await getSeityStatus();
      // Role gate (identity-login SSOT §1.1 invariant #3): the seity-bond
      // onboarding wall is for genuine creators only. Operator / admin /
      // director command-center logins are not bond-eligible and must never be
      // prompted to name-and-commit a bond — pass them straight through. The
      // engine decides this (`bond_eligible`); the field is optional for
      // back-compat, so only an explicit `false` skips the wall.
      if (status.bond_eligible === false) {
        setPhase('done');
        return;
      }
      if (!status.locked) {
        setPhase('done');
        return;
      }
      setPhase(status.initialized ? 'unlock' : 'setup');
    } catch (err) {
      setStatusError(friendlyError(err, 'Could not reach your seity. Try again.'));
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    void checkStatus();
  }, [checkStatus]);

  // ── First-use: set passphrase -> reveal recovery key once ──────────────────
  const onSetup = async (e: FormEvent) => {
    e.preventDefault();
    if (passphrase !== confirmPassphrase) {
      setFormError('Passphrases do not match.');
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const res = await unlockSeity(passphrase);
      if (res.first_use && res.recovery_key) {
        setRevealedRecoveryKey(res.recovery_key);
        setSavedConfirmed(false);
        setCopied(false);
        resetForms();
        setPhase('reveal');
      } else {
        // Defensive: engine reported already-initialized. Drop secrets, re-check.
        resetForms();
        if (res.unlocked) setPhase('done');
        else setPhase('unlock');
      }
    } catch (err) {
      setFormError(friendlyError(err, 'Could not set your passphrase. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  // ── Unlock: existing seity ─────────────────────────────────────────────────
  const onUnlock = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const res = await unlockSeity(passphrase);
      resetForms();
      if (res.unlocked) setPhase('done');
      else setFormError('That passphrase did not unlock your seity.');
    } catch (err) {
      setFormError(friendlyError(err, 'Wrong passphrase. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  // ── Recover: rotate via recovery key ───────────────────────────────────────
  const onRecover = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const res = await recoverSeity(recoveryKeyInput, newPassphrase);
      resetForms();
      if (res.unlocked || res.rotated) setPhase('done');
      else setFormError('Recovery did not complete. Check your recovery key.');
    } catch (err) {
      setFormError(friendlyError(err, 'Wrong recovery key. Check it and try again.'));
    } finally {
      setBusy(false);
    }
  };

  const onCopyRecoveryKey = async () => {
    if (!revealedRecoveryKey) return;
    try {
      await navigator.clipboard.writeText(revealedRecoveryKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (insecure context); the key is still visible.
      setCopied(false);
    }
  };

  const onConfirmSaved = () => {
    // Drop the plaintext key from memory the moment the user moves on.
    setRevealedRecoveryKey(null);
    setSavedConfirmed(false);
    setCopied(false);
    setPhase('done');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (phase === 'done') {
    return <>{children}</>;
  }

  if (phase === 'loading') {
    return (
      <GateShell>
        <div style={{ color: 'var(--sp-text-dim)' }}>Checking your seity...</div>
      </GateShell>
    );
  }

  if (phase === 'error') {
    return (
      <GateShell>
        <div style={cardStyle}>
          <div className="sp-brand mono" style={{ fontSize: 16, marginBottom: 16 }}>
            Seity unavailable
          </div>
          <div style={{ color: 'var(--sp-error)', fontSize: 13, marginBottom: 20 }}>
            {statusError}
          </div>
          <button
            type="button"
            className="sp-btn sp-btn-primary"
            style={{ width: '100%' }}
            onClick={() => void checkStatus()}
          >
            Retry
          </button>
        </div>
      </GateShell>
    );
  }

  if (phase === 'reveal' && revealedRecoveryKey) {
    return (
      <GateShell>
        <div style={cardStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--sp-warn)',
              marginBottom: 16,
            }}
          >
            <ShieldAlert size={18} />
            <span className="sp-brand mono" style={{ fontSize: 16 }}>
              Save your recovery key
            </span>
          </div>

          <p style={{ fontSize: 13, color: 'var(--sp-text)', lineHeight: 1.5, marginTop: 0 }}>
            This is the <strong>only</strong> way to recover your seity if you forget your
            passphrase. <strong>We cannot recover it for you.</strong> Store it somewhere safe — a
            password manager or written down offline.
          </p>
          <p style={{ fontSize: 12, color: 'var(--sp-text-dim)', marginTop: 0 }}>
            You will not be shown this key again.
          </p>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--sp-bg)',
              border: '1px solid var(--sp-border-strong)',
              borderRadius: 6,
              padding: '12px 14px',
              margin: '16px 0',
            }}
          >
            <code
              className="mono"
              style={{
                flex: 1,
                fontSize: 13,
                color: 'var(--sp-text)',
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
            >
              {revealedRecoveryKey}
            </code>
            <button
              type="button"
              className="sp-btn"
              aria-label="Copy recovery key"
              onClick={() => void onCopyRecoveryKey()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              fontSize: 13,
              color: 'var(--sp-text)',
              cursor: 'pointer',
              marginBottom: 20,
            }}
          >
            <input
              type="checkbox"
              checked={savedConfirmed}
              onChange={(e) => setSavedConfirmed(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>I have saved my recovery key somewhere safe.</span>
          </label>

          <button
            type="button"
            className="sp-btn sp-btn-primary"
            style={{ width: '100%' }}
            disabled={!savedConfirmed}
            onClick={onConfirmSaved}
          >
            Continue
          </button>
        </div>
      </GateShell>
    );
  }

  if (phase === 'recover') {
    return (
      <GateShell>
        <form onSubmit={onRecover} style={cardStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <KeyRound size={18} style={{ color: 'var(--sp-accent)' }} />
            <span className="sp-brand mono" style={{ fontSize: 16 }}>
              Recover your seity
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--sp-text-dim)', marginTop: 0, marginBottom: 20 }}>
            Enter the recovery key you saved when you first set up your seity, then choose a new
            passphrase.
          </p>

          <PassphraseField
            id="seity-recovery-key"
            label="Recovery key"
            value={recoveryKeyInput}
            onChange={setRecoveryKeyInput}
            autoComplete="off"
            autoFocus
          />
          <div style={{ height: 12 }} />
          <PassphraseField
            id="seity-new-passphrase"
            label="New passphrase"
            value={newPassphrase}
            onChange={setNewPassphrase}
            autoComplete="new-password"
          />

          {formError && (
            <div style={{ color: 'var(--sp-error)', fontSize: 12, marginTop: 12 }}>{formError}</div>
          )}

          <div style={{ height: 20 }} />
          <button
            type="submit"
            className="sp-btn sp-btn-primary"
            disabled={busy}
            style={{ width: '100%' }}
          >
            {busy ? 'Recovering...' : 'Recover and unlock'}
          </button>
          <button
            type="button"
            className="sp-btn"
            style={{ width: '100%', marginTop: 10 }}
            onClick={() => {
              resetForms();
              setPhase('unlock');
            }}
          >
            Back to unlock
          </button>
        </form>
      </GateShell>
    );
  }

  if (phase === 'setup') {
    return (
      <GateShell>
        <form onSubmit={onSetup} style={cardStyle}>
          <div className="sp-brand mono" style={{ fontSize: 16, marginBottom: 8 }}>
            Set your seity passphrase
          </div>
          <p style={{ fontSize: 12, color: 'var(--sp-text-dim)', marginTop: 0, marginBottom: 20 }}>
            Your seity is encrypted with a passphrase only you know. Choose one you will remember —
            you will get a one-time recovery key next.
          </p>

          <PassphraseField
            id="seity-passphrase"
            label="Passphrase"
            value={passphrase}
            onChange={setPassphrase}
            autoComplete="new-password"
            autoFocus
          />
          <div style={{ height: 12 }} />
          <PassphraseField
            id="seity-passphrase-confirm"
            label="Confirm passphrase"
            value={confirmPassphrase}
            onChange={setConfirmPassphrase}
            autoComplete="new-password"
          />

          {formError && (
            <div style={{ color: 'var(--sp-error)', fontSize: 12, marginTop: 12 }}>{formError}</div>
          )}

          <div style={{ height: 20 }} />
          <button
            type="submit"
            className="sp-btn sp-btn-primary"
            disabled={busy || passphrase.length === 0}
            style={{ width: '100%' }}
          >
            {busy ? 'Setting up...' : 'Set passphrase'}
          </button>
        </form>
      </GateShell>
    );
  }

  // phase === 'unlock'
  return (
    <GateShell>
      <form onSubmit={onUnlock} style={cardStyle}>
        <div className="sp-brand mono" style={{ fontSize: 16, marginBottom: 8 }}>
          Unlock your seity
        </div>
        <p style={{ fontSize: 12, color: 'var(--sp-text-dim)', marginTop: 0, marginBottom: 20 }}>
          Enter your passphrase to unlock your seity for this session.
        </p>

        <PassphraseField
          id="seity-unlock-passphrase"
          label="Passphrase"
          value={passphrase}
          onChange={setPassphrase}
          autoComplete="current-password"
          autoFocus
        />

        {formError && (
          <div style={{ color: 'var(--sp-error)', fontSize: 12, marginTop: 12 }}>{formError}</div>
        )}

        <div style={{ height: 20 }} />
        <button
          type="submit"
          className="sp-btn sp-btn-primary"
          disabled={busy || passphrase.length === 0}
          style={{ width: '100%' }}
        >
          {busy ? 'Unlocking...' : 'Unlock'}
        </button>
        <button
          type="button"
          className="sp-btn"
          style={{
            width: '100%',
            marginTop: 12,
            background: 'transparent',
            border: 0,
            color: 'var(--sp-accent)',
          }}
          onClick={() => {
            resetForms();
            setPhase('recover');
          }}
        >
          Forgot your passphrase? Recover with your recovery key
        </button>
      </form>
    </GateShell>
  );
}
